import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const DEFAULT_PER_PAGE = 24;

/** @type {number | undefined} */
let scheduleTimer;

/** @type {MutationObserver | undefined} */
let gridObserver;

/**
 * @param {HTMLElement} resultsList
 * @returns {number}
 */
function getProductsPerPage(resultsList) {
  const perPage = Number(resultsList.dataset.productsPerPage || DEFAULT_PER_PAGE);
  return Number.isFinite(perPage) && perPage > 0 ? perPage : DEFAULT_PER_PAGE;
}

/**
 * @param {HTMLElement} resultsList
 * @returns {string[]}
 */
function getPinnedProductIds(resultsList) {
  const raw = resultsList.dataset.pinnedProductIds || '';

  return raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * @param {HTMLElement} resultsList
 * @returns {Promise<Element[]>}
 */
async function collectPinnedCards(resultsList) {
  const sectionId = resultsList.getAttribute('section-id');
  const pinnedIds = getPinnedProductIds(resultsList);
  const productsCount = Number(resultsList.dataset.productsCount || 0);

  if (!sectionId || pinnedIds.length === 0 || productsCount <= 0) return [];

  const perPage = getProductsPerPage(resultsList);
  const pageCount = Math.max(1, Math.ceil(productsCount / perPage));
  const requests = [];

  for (let page = 1; page <= pageCount; page += 1) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    requests.push(sectionRenderer.getSectionHTML(sectionId, false, url));
  }

  const pagesHtml = await Promise.all(requests);
  /** @type {Map<string, Element>} */
  const cardsById = new Map();

  for (const html of pagesHtml) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cards = doc.querySelectorAll('[ref="cards[]"]');

    for (const card of cards) {
      const productId = card.getAttribute('data-product-id');

      if (!productId || cardsById.has(productId)) continue;

      cardsById.set(productId, card);
    }
  }

  return pinnedIds.map((id) => cardsById.get(id)).filter((card) => card instanceof Element);
}

/**
 * @param {HTMLElement} grid
 * @param {Set<string>} pinnedIds
 */
function removePinnedDuplicates(grid, pinnedIds) {
  const cards = grid.querySelectorAll('[ref="cards[]"]');

  for (const card of cards) {
    const productId = card.getAttribute('data-product-id');

    if (productId && pinnedIds.has(productId)) {
      card.remove();
    }
  }
}

/**
 * @param {HTMLElement} grid
 * @param {Set<string>} pinnedIds
 */
function observeGridForPinnedDuplicates(grid, pinnedIds) {
  gridObserver?.disconnect();

  gridObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        const candidates = [];

        if (node.matches('[ref="cards[]"]')) {
          candidates.push(node);
        } else {
          candidates.push(...node.querySelectorAll('[ref="cards[]"]'));
        }

        for (const card of candidates) {
          const productId = card.getAttribute('data-product-id');

          if (productId && pinnedIds.has(productId)) {
            card.remove();
          }
        }
      }
    }
  });

  gridObserver.observe(grid, { childList: true });
}

/**
 * @param {HTMLElement} resultsList
 * @param {Element[]} pinnedCards
 */
function applyPinnedCards(resultsList, pinnedCards) {
  const grid = resultsList.querySelector('[ref="grid"]');

  if (!(grid instanceof HTMLElement) || pinnedCards.length === 0) return;

  const pinnedIds = new Set(
    pinnedCards
      .map((card) => card.getAttribute('data-product-id'))
      .filter((id) => Boolean(id))
  );

  removePinnedDuplicates(grid, pinnedIds);

  const fragment = document.createDocumentFragment();

  for (const card of pinnedCards) {
    const clone = document.importNode(card, true);

    if (clone instanceof HTMLElement) {
      clone.setAttribute('data-page', '1');
      clone.setAttribute('data-pinned', 'true');
      fragment.appendChild(clone);
    }
  }

  grid.prepend(fragment);
  observeGridForPinnedDuplicates(grid, pinnedIds);
}

/**
 * @param {HTMLElement} resultsList
 */
async function ensurePinnedProducts(resultsList) {
  if (resultsList.dataset.productPinning !== 'true') return;
  if (resultsList.dataset.pinningBusy === 'true') return;

  const sectionId = resultsList.getAttribute('section-id');

  if (!sectionId) return;

  resultsList.dataset.pinningBusy = 'true';

  try {
    const pinnedCards = await collectPinnedCards(resultsList);
    const list = document.querySelector(`results-list[section-id="${sectionId}"]`);

    if (!(list instanceof HTMLElement)) return;

    applyPinnedCards(list, pinnedCards);
  } catch (error) {
    console.error('[product-pinning] Failed to apply pinned products', error);
  } finally {
    const list = document.querySelector(`results-list[section-id="${sectionId}"]`);

    if (list instanceof HTMLElement) {
      list.dataset.pinningBusy = 'false';
    }
  }
}

function schedulePinning() {
  window.clearTimeout(scheduleTimer);
  scheduleTimer = window.setTimeout(() => {
    const lists = document.querySelectorAll('results-list[data-product-pinning="true"]');

    for (const list of lists) {
      if (!(list instanceof HTMLElement)) continue;
      ensurePinnedProducts(list);
    }
  }, 200);
}

function initProductPinning() {
  if (window.__pbProductPinningInit) return;

  window.__pbProductPinningInit = true;
  schedulePinning();
  document.addEventListener(ThemeEvents.FilterUpdate, schedulePinning);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductPinning, { once: true });
} else {
  initProductPinning();
}
